import React, { useCallback } from 'react';
import getConfig from 'next/config';
import { Row, Divider, Col } from 'antd';
import styled from 'styled-components';
import Content from 'layout/Basic/Content';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { useState } from 'react';
import OurIssueSection from 'components/OurIssueSection';
import { GetServerSideProps } from 'next';
import BasicLayout from 'layout/Basic';
import { State } from 'reducer/reducer';
import { getToken, isTokenValid } from 'lib/authentication';
import { logout, openLoginModal } from 'reducer/actions';
import Sider from 'layout/Basic/Sider';
import { convertProps } from 'lib/parseAPIResponse';
import { ExpandNavibox, HalfPage, Rectangle, SkyscraperMobile, Wideboard } from 'components/Ads';
import { SectionTitle } from 'components/Section';
import Newsletter from 'containers/Newsletter';
import ToC from 'components/TableOfContent/Content';
import Section from 'components/Section';
import ArticleCarousel from 'components/ArticleCarousel';
import Image from 'components/Image';
import { NextSeo } from 'next-seo';
import { EventsSection } from 'components/EventsSection';

const Title = styled.h3`
    font-size: 32px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.25;
    letter-spacing: normal;
    text-align: left;
    color: ${({ theme }) => theme.colors.primary};
`;

const CoverCol = styled(Col)`
    z-index: 1;
    @media (max-width: 992px) {
        margin: auto;
    }
`;

const CoverContainer = styled.div`
    margin-bottom: 4rem;
    margin-left: 10%;
    @media (max-width: 992px) {
        margin: auto;
    }
`;
const Button = styled.div`
    border-radius: 22px;
    border: solid 2px #cb212f;
    background-color: #cb212f;
    color: ${({ theme }) => theme.colors.white};

    font-size: 18px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.44;
    letter-spacing: normal;
    text-align: left;
    padding: 1rem 2rem;
`;

const OpeningBackground = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 50%;
    background-color: ${({ theme }) => theme.colors.grey};
`;

interface Props {
    issue: Issue;
    tableOfContent: ToC[];
    events: Occurrence[];
    eventsPatronate: Occurrence[];
    latestIssue: Issue;
    recentArticles: Article[];
    ads: PageAds;
}

const InteractiveOverlayText = styled.div`
    font-size: 32px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.25;
    letter-spacing: normal;
    text-align: center;
    color: ${({ theme }) => theme.colors.white};
    button {
        cursor: pointer;
        background: transparent;
        border: none;
        color: ${({ theme }) => theme.colors.white};
        text-decoration: underline;
        :hoer {
            color: ${({ theme }) => theme.colors.primary};
        }
    }
`;

export const InteractiveOverlay = ({ onLogin }): React.JSX.Element => (
    <div
        style={{
            position: 'absolute',
            zIndex: 10,
            display: 'flex',
            justifyItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            opacity: 0.9,
            backgroundColor: 'grey'
        }}
    >
        <div
            style={{
                display: 'flex',
                alignItems: 'center'
            }}
        >
            <InteractiveOverlayText>
                <button onClick={onLogin}>Zobacz e-wydanie</button>
            </InteractiveOverlayText>
        </div>
    </div>
);

export const CurrentIssuePage = ({
    recentArticles,
    issue,
    tableOfContent,
    latestIssue,
    eventsPatronate,
    events,
    ads
}: Props): React.JSX.Element => {
    const dispatch = useDispatch();
    const { url } = getConfig().publicRuntimeConfig;
    const isLoggedIn = useSelector((state: State) => state.isLoggedIn);
    const [token, setToken] = useState<string | null>(null);

    useEffect(() => {
        const currentToken = getToken();
        setToken((prevToken) => {
            if (prevToken === currentToken) {
                return prevToken;
            }
            return currentToken;
        });
    }, [isLoggedIn]);

    const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
        if (issue.whitePaper.isSecure && !isTokenValid()) {
            event.preventDefault();
            dispatch(logout());
            dispatch(openLoginModal());
            return;
        }
    };

    const handleOpenEIssueClick = (event: React.MouseEvent<HTMLElement>) => {
        if (issue.eIssueUrl && issue.eIssueUrlProtected && !isTokenValid()) {
            event.preventDefault();
            dispatch(openLoginModal());
            return;
        }
    };

    const isAvailable = useCallback(() => {
        return issue.eIssueUrlProtected ? isTokenValid() : true;
    }, [token, issue.eIssueUrlProtected]);

    const { skyscraper } = ads;
    return (
        <BasicLayout ads={ads}>
            <NextSeo title={issue.name} canonical={`${url}/${issue.seoSlug}`} />
            <Row gutter={16}>
                <Content>
                    <Row>
                        <Col span={24}>
                            <SectionTitle title={issue.name} />
                        </Col>
                    </Row>
                    <Section>
                        <Col span={24}>
                            <OpeningBackground />
                            <Row>
                                <CoverCol>
                                    <CoverContainer>
                                        {issue.eIssueUrl && (
                                            <a
                                                href={isAvailable() && issue.eIssueUrl}
                                                target="_blank"
                                                onClick={handleOpenEIssueClick}
                                                rel="nofollow noreferrer"
                                            >
                                                <Image
                                                    type="issue"
                                                    width={370}
                                                    height={529}
                                                    alt={issue.name}
                                                    src={issue.cover}
                                                />
                                            </a>
                                        )}
                                        {!issue.eIssueUrl && (
                                            <Image
                                                type="issue"
                                                width={370}
                                                height={529}
                                                alt={issue.name}
                                                src={issue.cover}
                                            />
                                        )}
                                    </CoverContainer>
                                </CoverCol>
                                <Col xs={24} md={10}>
                                    <Row
                                        align="bottom"
                                        style={{ height: '100%', padding: '0 1rem' }}
                                    >
                                        <Col span={24} style={{ margin: '2rem 0 4rem' }}>
                                            {issue.eIssueUrl && (
                                                <div
                                                    style={{
                                                        display: 'block',
                                                        padding: '1rem 2rem'
                                                    }}
                                                >
                                                    <a
                                                        href={isAvailable() && issue.eIssueUrl}
                                                        target="_blank"
                                                        onClick={handleOpenEIssueClick}
                                                        rel="nofollow noreferrer"
                                                    >
                                                        <Button>
                                                            Obejrzyj jako e-book
                                                            {issue.eIssueUrlProtected &&
                                                                !isLoggedIn && (
                                                                    <p style={{ margin: 0 }}>
                                                                        (musisz być zalogowany)
                                                                    </p>
                                                                )}
                                                        </Button>
                                                    </a>
                                                </div>
                                            )}
                                            {issue.whitePaper && (
                                                <div
                                                    style={{
                                                        display: 'block',
                                                        padding: '1rem 2rem'
                                                    }}
                                                >
                                                    <a
                                                        href={`${issue.whitePaper.downloadLink}?bearer=${token}`}
                                                        onClick={handleDownloadClick}
                                                    >
                                                        <Button>
                                                            Pobierz PDF
                                                            {issue.whitePaper.isSecure &&
                                                                !isLoggedIn && (
                                                                    <p style={{ margin: 0 }}>
                                                                        (musisz być zalogowany)
                                                                    </p>
                                                                )}
                                                        </Button>
                                                    </a>
                                                </div>
                                            )}
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </Col>
                    </Section>
                    <Row>
                        <Col xs={24} md={12}>
                            <SkyscraperMobile id={skyscraper[0]} margin="15px auto 0 auto" />
                        </Col>
                        <Col xs={24} md={12}>
                            <SkyscraperMobile id={skyscraper[1]} margin="15px auto 0 auto" />
                        </Col>
                    </Row>
                    <Section>
                        <Col span={24}>
                            <Title id="spis-tresci">{issue.name} Spis treści</Title>
                        </Col>
                    </Section>
                    <ToC tableOfContent={tableOfContent} />
                    <Row justify="center">
                        <Col>
                            <ExpandNavibox id={ads.navibox[0]} />
                        </Col>
                    </Row>
                    <Section gutter={[16, 32]} justify="center">
                        <Col span={24}>
                            <Divider />
                        </Col>
                        <Col xs={24} md={20}>
                            <Newsletter />
                        </Col>
                        <Col span={24}>
                            <Divider />
                        </Col>
                    </Section>

                    <Section>
                        <Col span={24}>
                            <EventsSection events={events} eventsPatronate={eventsPatronate} />
                        </Col>
                    </Section>
                </Content>
                <Sider>
                    <Row gutter={[16, 32]}>
                        <Col span={24}>
                            <SectionTitle title="OSTATNIE ARTYKUŁY" />
                            <ArticleCarousel
                                items={recentArticles}
                                itemsPerRow={1}
                                seeMore={false}
                            />
                        </Col>
                        <Col span={24}>
                            <Rectangle id={ads.rectangle[0]} />
                        </Col>
                        <Col span={24}>
                            <HalfPage id={ads.halfpage[0]} />
                        </Col>
                        <Col span={24}>
                            <Section>
                                <OurIssueSection issue={latestIssue} />
                            </Section>
                        </Col>
                    </Row>
                </Sider>
            </Row>
            <Section justify="center">
                <Col>
                    <Wideboard id={ads.wideboardBottom[0]} />
                </Col>
            </Section>
        </BasicLayout>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ params }) => {
    const { apiUrl } = getConfig().publicRuntimeConfig;
    const slug = params.slug === 'aktualny-numer' ? 'current' : params.slug;
    return Promise.all([
        fetch(`${apiUrl}/public/api/issues/${slug}`),
        fetch(`${apiUrl}/public/api/naszadsik?path=archiwum`)
    ]).then(async ([res1, res2]) => {
        const data = await res1.json();
        if (data.code === 404) {
            return {
                notFound: true
            };
        }
        const ads = await res2.json();

        return { props: { ...convertProps(ads), ...convertProps(data) } };
    });
};

export default CurrentIssuePage;
